import { DataType, IDrawingShapeData } from '@tencent/slide-sdk-model';
import { ShapeTag } from './define';
import { StructWithList } from './parse-page';
import { marked } from 'marked';
import { parseJson } from './utils/utils';

interface IDMapBase {
  title?: string;
  image?: string;
  content?: string;
}

export interface IDMap extends IDMapBase {
  list?: IDMapBase[];
  chart?: string;
}

export type Schema = {
  type: string;
  properties: Record<string, any>;
};

const newSchema = (): Schema => ({
  type: 'object',
  properties: {},
});

export const createSchemaFromIdMap = (idMap: IDMap) => {
  const schema = newSchema();
  if (idMap.title) {
    schema.properties.title = {
      type: 'string',
      title: 'title',
      default: '',
      canOmit: false,
    };
  }
  if (idMap.content) {
    schema.properties.title = {
      type: 'string',
      title: 'content',
      default: '',
      canOmit: false,
    };
  }

  if (idMap.image) {
    schema.properties.title = {
      type: 'string',
      title: 'image',
      default: '',
      canOmit: false,
    };
  }

  if (idMap.list) {
    schema.properties.list = {
      type: 'array',
      title: 'list',
      maxLength: idMap.list.length,
      minLength: idMap.list.length,
      items: createSchemaFromIdMap(idMap.list[0]),
    };
  }

  return schema;
};

export const getShapeTag = (shape: IDrawingShapeData): ShapeTag | null => {
  const { descr } = shape.nvPr;

  return descr ? parseJson<ShapeTag>(descr) : null;
};

const addSchema = (shape: IDrawingShapeData, shapeTag: ShapeTag, schema: Schema, idMap: IDMap) => {
  if (!shapeTag?.isTemplate) return;

  const shapeType = shapeTag.type;

  if (shapeType === 'list') {
    if (shape.type !== DataType.GroupShape) return;

    idMap[shapeType] = [];
    const childSchema = newSchema();
    schema.properties.list = {
      type: 'array',
      title: shape.nvPr.name,
      default: '',
      maxLength: shape.shapeList.length,
      minLength: shapeTag.minItemsCount < 0 ? shape.shapeList.length : shapeTag.minItemsCount,
      items: childSchema,
      'x-decorator': 'FormItem',
      'x-component': 'ArrayCards',
    };

    // const dev = false;
    // if (dev) {
    //     schema.properties.list.properties = {
    //         '7ukbfhaxvfy': {
    //             type: 'void',
    //             title: 'Addition',
    //             'x-component': 'ArrayCards.Addition',
    //             'x-component-props': {},
    //             'x-designable-id': '7ukbfhaxvfy',
    //             'x-index': 0,
    //         },
    //     };
    // }

    shape.shapeList.forEach((listItem) => {
      const childIdMap: IDMap = {};
      idMap[shapeType]!.push(childIdMap);
      traverseTags([listItem], childSchema, childIdMap);
    });

    return;
  }

  idMap[shapeType] = shape.id;
  schema.properties[shapeType] = {
    type: 'string',
    title: shape.nvPr.name,
    default: '',
    canOmit: shapeTag.canOmit ?? false,
    'x-decorator': 'FormItem',
    'x-component': 'Input',
  };
};

export const traverseTags = (shapeList: IDrawingShapeData[], schema = newSchema(), idMap: IDMap = {}) => {
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let index = 0; index < shapeList.length; index++) {
    const shape = shapeList[index];

    const shapeTag = getShapeTag(shape);
    if (shapeTag?.isTemplate) {
      addSchema(shape, shapeTag, schema, idMap);
      continue;
    }

    if (shape.type === DataType.GroupShape) {
      traverseTags(shape.shapeList, schema, idMap);
    }
  }

  return {
    schema,
    idMap,
  };
};

export const makeTestStructData = (shapeList: IDrawingShapeData[]): StructWithList => {
  const { idMap } = traverseTags(shapeList);
  // const struct: StructWithList = {};
  const textMap = makeTestData(idMap);
  return trans(textMap);
};

const trans = (idMap: IDMap): StructWithList => {
  const struct: StructWithList = {};
  Object.keys(idMap).forEach((key) => {
    if (!idMap[key]) return;

    if (key === 'title') {
      struct[key] = createTextToken(idMap[key]!);
    }
    if (key === 'content') {
      struct[key] = [createTextToken(idMap[key]!)];
    }
    if (key === 'image') {
      struct[key] = createImageToken(idMap[key]!);
    }
    if (key === 'list') {
      struct[key] = idMap[key]!.map(trans);
    }
  });

  return struct;
};

const createTextToken = (text: string): marked.Tokens.Paragraph => ({
  type: 'paragraph',
  raw: text,
  text: text,
  tokens: [
    {
      type: 'text',
      raw: text,
      text: text,
    },
  ],
});

const createImageToken = (url: string): marked.Tokens.Image => ({
  type: 'image',
  raw: url,
  href: url,
  title: '',
  text: '',
});

const makeTestData = (
  idMap: IDMap,
  options: { contentLength: number } = { contentLength: 80 },
  nestedIndex?: number,
) => {
  Object.keys(idMap).forEach((key) => {
    if (key === 'title') {
      if (!nestedIndex) {
        idMap[key] = '5.1 这是一个十字的页标题';
      } else {
        idMap[key] = `#${nestedIndex}这是一个列标题`;
      }
    }
    if (key === 'content') {
      const text = '这是一段十字的正文。';
      idMap[key] = Array(Math.round(options.contentLength / 10))
        .fill('')
        .map(() => text)
        .join('');
    }
    if (key === 'image') {
      idMap[key] = 'https://docimg8.docs.qq.com/image/AgAABTW7Ya4dU-c28eZGOry4TAxcAokL.gif?w=1920&h=1080';
    }
    if (key === 'list') {
      idMap[key]?.map((idmap, index) => makeTestData(idmap, options, index + 1));
    }
  });
  return idMap;
};
